import { Page, Text, View, Document, StyleSheet, Image, Font as _font, PDFDownloadLink } from '@react-pdf/renderer';
import { useUser } from '@utmb/contexts/AuthContext';
import { Font } from '@utmb/design-system/text/font';
import { EventView } from '@utmb/types/index';
import ShuttleLineView from '@utmb/types/view/ShuttleLineView';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { ReactElement } from 'react';
import styles from './bus-reservation.module.scss';

_font.register({ family: 'Futura', src: 'https://pdf.utmb.world/raw/upload/assets/font/FuturaPTBook.woff' });
_font.register({ family: 'FuturaBold', src: 'https://pdf.utmb.world/raw/upload/assets/font/FuturaPTBold.woff' });

const pdf = StyleSheet.create({
    page: {
        backgroundColor: '#ffffff',
    },
    content: {
        border: '5px solid #324631',
        margin: 5,
    },
    header: {
        backgroundColor: 'white',
        height: '70px',
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    subHeader: {
        backgroundColor: '#324631',
        height: '70px',
        padding: '5px 0',
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginLeft: -1,
        marginRight: -1,
    },
    headerTextBold: {
        color: '#324631',
        textTransform: 'uppercase',
        fontSize: '20px',
        fontFamily: 'FuturaBold',
    },
    headerText: {
        color: '#324631',
        textTransform: 'uppercase',
        fontSize: '16px',
        fontFamily: 'Futura',
    },
    subHeaderTextBold: {
        color: 'white',
        textTransform: 'uppercase',
        fontSize: '20px',
        fontFamily: 'FuturaBold',
    },
    subHeaderText: {
        color: 'white',
        textTransform: 'uppercase',
        fontSize: '16px',
        fontFamily: 'Futura',
    },
    passTextBold: {
        color: 'white',
        textTransform: 'uppercase',
        fontSize: '14px',
        fontFamily: 'FuturaBold',
    },
    passText: {
        color: 'white',
        textTransform: 'uppercase',
        fontSize: '12px',
        fontFamily: 'Futura',
    },
    body: {
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        fontFamily: 'Futura',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        gap: 10,
        marginBottom: 10,
    },
    simpleRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        flexWrap: 'wrap',
        marginLeft: 10,
    },
    flex: {
        display: 'flex',
        flexDirection: 'row',
        gap: 20,
        justifyContent: 'space-between',
        width: '100%',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
    bodyText: {
        color: '#324631',
        textTransform: 'uppercase',
        fontSize: '12px',
        fontFamily: 'Futura',
    },
    bodyTextBold: {
        color: '#324631',
        textTransform: 'uppercase',
        fontSize: '12px',
        fontFamily: 'FuturaBold',
    },
    imageTitle: {
        width: '50px',
        height: '50px',
        position: 'absolute',
        right: 4,
        zIndex: 2,
        top: 4,
    },
    logo: {
        width: '75px',
        height: '60px',
        position: 'absolute',
        left: '2mm',
        zIndex: 2,
        top: '2mm',
        objectFit: 'contain',
    },
    imagePass: {
        width: '70px',
        height: '50px',
        position: 'absolute',
        right: 0,
        zIndex: 2,
        bottom: 0,
    },
    imageWarning: {
        width: '35px',
        height: '35px',
    },
    uws: {
        color: '#324631',
        textTransform: 'uppercase',
        fontSize: '18px',
        fontFamily: 'FuturaBold',
    },
    mobility: {
        color: '#324631',
        textTransform: 'uppercase',
        fontSize: '24px',
        fontFamily: 'FuturaBold',
    },
    warning: {
        paddingRight: 5,
        margin: 0,
        backgroundColor: '#324631',
        border: 'none',
        height: '15mm',
        alignItems: 'center',
        alignContent: 'center',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginLeft: -1,
        marginRight: -1,
    },
    warningCol1: {
        display: 'flex',
        flexDirection: 'row',
        gap: 10,
        marginLeft: 10,
    },
    warningCol2: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        justifyContent: 'center',
        alignItems: 'center',
        paddingRight: 10,
    },
    footer: {
        backgroundColor: 'white',
        height: '35mm',
        alignItems: 'flex-start',
        alignContent: 'center',
        justifyContent: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    imageMobility: {
        width: '55mm',
        height: '35mm',
        position: 'absolute',
        right: 0,
        zIndex: 2,
        bottom: 0,
    },
    columnMobility: {
        display: 'flex',
        flexDirection: 'column',
        marginLeft: 10,
        height: '35mm',
        gap: 3,
        alignContent: 'center',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
});

interface PdfRunnerShuttleProps {
    shuttleLines: ShuttleLineView[];
    eventView: EventView;
    year: number;
}

export const PdfRunnerShuttleButton = (props: PdfRunnerShuttleProps): ReactElement => {
    const { shuttleLines, year, eventView } = props;
    const { locale } = useRouter();
    const { t, i18n } = useTranslation();
    const { logo } = eventView;

    const { me } = useUser();

    const enT = i18n.getFixedT('en');

    console.log(logo);

    const PdfRunnerTicket = () => (
        <Document>
            <Page size="A4" style={pdf.page}>
                {shuttleLines && shuttleLines.length > 0 ? (
                    <View style={pdf.content}>
                        <View style={pdf.header}>
                            <Text style={pdf.headerTextBold}>
                                {enT('busReservation.shuttleRunner.pdf.title')} {year}
                            </Text>
                            {locale !== 'en' ? <Text style={pdf.headerText}>{t('busReservation.shuttleRunner.pdf.title')}</Text> : <></>}
                            <Image style={pdf.imageTitle} src={'https://pdf.utmb.world/image/upload/v1686312316/assets/pdf/runner_bg_img.png'} />
                        </View>
                        <View style={pdf.subHeader}>
                            <Image style={pdf.logo} src={`https://pdf.utmb.world/${logo}`} />
                            <Text style={pdf.subHeaderTextBold}>{enT('busReservation.shuttleRunner.pdf.subtitle')}</Text>
                            {locale !== 'en' ? <Text style={pdf.subHeaderText}>{t('busReservation.shuttleRunner.pdf.subtitle')}</Text> : <></>}
                        </View>
                        <View style={pdf.body}>
                            <View style={pdf.row}>
                                <View style={pdf.column}>
                                    <Text style={pdf.bodyTextBold}>{enT('busReservation.accompanyingPass.line')}</Text>
                                    {locale !== 'en' ? <Text style={pdf.bodyText}>{t('busReservation.accompanyingPass.line')}</Text> : <></>}
                                </View>
                                <View style={pdf.column}>
                                    {shuttleLines.map((shuttleLine) => (
                                        <React.Fragment key={`shuttleLine${shuttleLine.id}`}>
                                            {shuttleLine.selectedShuttleId ? (
                                                <View style={pdf.column}>
                                                    <Text style={pdf.bodyText}>
                                                        {shuttleLine.name} -{' '}
                                                        {shuttleLine.shuttles?.find((shuttle) => shuttle.id === shuttleLine.selectedShuttleId)?.name}
                                                    </Text>
                                                    <View style={pdf.row}>
                                                        <View style={pdf.column}>
                                                            <Text style={pdf.bodyTextBold}>
                                                                {enT(`busReservation.accompanyingPass.${shuttleLine.type}LeaveAt`)}
                                                            </Text>
                                                            {locale !== 'en' ? (
                                                                <Text style={pdf.bodyText}>
                                                                    {t(`busReservation.accompanyingPass.${shuttleLine.type}LeaveAt`)}
                                                                </Text>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </View>
                                                        <Text style={pdf.bodyText}>
                                                            {shuttleLine.shuttles?.find((shuttle) => shuttle.id === shuttleLine.selectedShuttleId)?.scheduleIso}
                                                        </Text>
                                                    </View>
                                                </View>
                                            ) : (
                                                <></>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </View>
                            </View>
                            <View style={pdf.row}>
                                <View style={pdf.column}>
                                    <Text style={pdf.bodyTextBold}>{enT('common.fullname')}:</Text>
                                    {locale !== 'en' ? <Text style={pdf.bodyText}>{t('common.fullname')}</Text> : <></>}
                                </View>
                                <Text style={pdf.bodyText}>{me!.fullname}</Text>
                            </View>
                        </View>
                        <View style={pdf.warning}>
                            <View style={pdf.warningCol1}>
                                <Image style={pdf.imageWarning} src={'https://pdf.utmb.world/image/upload/v1686311791/assets/pdf/point_bg_img.png'} />
                                <View style={pdf.column}>
                                    <Text style={pdf.passTextBold}>{enT('busReservation.accompanyingPass.toTakeTheBus')}</Text>
                                    {locale !== 'en' ? <Text style={pdf.passText}>{t('busReservation.accompanyingPass.toTakeTheBus')}</Text> : <></>}
                                </View>
                            </View>
                            <View style={pdf.warningCol2}>
                                <Text style={pdf.passTextBold}>{enT('busReservation.shuttleRunner.pdf.bibAndPass')}</Text>
                                {locale !== 'en' ? <Text style={pdf.passText}>{t('busReservation.shuttleRunner.pdf.bibAndPass')}</Text> : <></>}
                            </View>
                        </View>
                        <View style={pdf.footer}>
                            <View style={pdf.columnMobility}>
                                <Text style={pdf.uws}>UTMB WORLD SERIES</Text>
                                <Text style={pdf.mobility}>MOBILITY</Text>
                            </View>
                            <Image style={pdf.imageMobility} src={'https://pdf.utmb.world/image/upload/v1686312316/assets/pdf/bus_bg_img.png'} />
                        </View>
                    </View>
                ) : (
                    <></>
                )}
            </Page>
        </Document>
    );

    return (
        <PDFDownloadLink document={<PdfRunnerTicket />} fileName={`shuttleTicket${me!.id}.pdf`} className={styles.buttonSpecial}>
            <Font.OxaniumMedium desktop="16" mobile="16" className={styles.text}>
                {t('busReservation.shuttleRunner.downloadPDF')}
            </Font.OxaniumMedium>
        </PDFDownloadLink>
    );
};
